/**
 * THE FLAG. A tab welded to the edge of somebody else's website.
 *
 * This is a port of the reference widget's `FlagButton` and the LOOK is meant to be
 * recognisable rather than improved: the same #fac800, the same 40×160 tab with two
 * rounded corners facing into the page, the same 180×68 landscape pill, the same
 * two-part shadow, the same rotated label. A customer who has had the old widget on
 * their site for a year must not notice a redesign. Everything that IS different is
 * listed below, because "it looked like this before" is the only reason several of
 * these numbers are the numbers they are, and a future reader deserves to know
 * which ones are arbitrary and which ones are load-bearing.
 *
 * ---------------------------------------------------------------------------
 * WHAT CHANGED FROM THE REFERENCE
 *
 * 1. IT MIRRORS. The reference hard-codes `left-0`. A tab is a tab on either edge,
 *    and mirroring is not just swapping a `left` for a `right`: the rounded corners
 *    move, the shadow's horizontal offset flips sign, the rotated label changes
 *    direction (-90° reads bottom-to-top and is right for a left tab; a right tab
 *    wants +90°, top-to-bottom), and the expanded row reverses so the stop glyph
 *    stays nearest the edge. Get one of the five wrong and it looks like a bug.
 *
 * 2. NO FRAMER-MOTION, ON PURPOSE. The reference springs the size morph with
 *    `stiffness: 300, damping: 30`. That spring settles in roughly 400 ms with a
 *    barely-visible overshoot, so it is reproduced with a 400 ms cubic-bezier whose
 *    final control point sits above 1 — see `SPRING`. This is a widget that ships
 *    inside a script tag on a stranger's page; an animation library is 30 kB of
 *    someone else's bundle for one width transition.
 *
 * 3. TWO BAR METERS, LIKE THE REFERENCE — AND THIS IS A REVERSAL. The first port
 *    replaced the reference's two bar visualisers with one orb, on the argument
 *    that two meters in 100 px cannot answer "whose turn is it". Product decision
 *    2026-08-23: the flag shows the reference's meters — agent left, user right,
 *    ten black bars each, thin centred line at silence — and the orb stays out of
 *    this variant entirely. The port lives in flag-bars.tsx, driven by the same
 *    spectrum the conversation bar draws. One honest wrinkle: during `connecting`
 *    there is nothing to meter, so the pill shows the word instead — the meters
 *    appear when the call is live, which is also when the reference first showed
 *    them (it stayed collapsed until connect resolved).
 *
 * 4. IT IS A REAL BUTTON. The reference is a `<div>` with an `onClick`: no keyboard
 *    access, no focus ring, no accessible name, nothing announced when the call
 *    connects. This one is a `<button>` with an `aria-label` that changes with the
 *    phase and a polite live region beside it — see `statusSentence` for why the
 *    live text deliberately does NOT follow listening/thinking/speaking.
 *
 * 5. ON A PHONE IT STOPS BEING A TAB. A 180 px pill hanging off the edge of a
 *    375 px screen leaves 195 px of page and looks like an accident. Under
 *    `NARROW_MAX_PX` the expanded state becomes a full-width bar pinned to the
 *    bottom, which is where a phone user's thumb already is. The collapsed tab
 *    stays a tab: 160 px of edge is fine, and it is what the customer bought.
 *
 * 6. NO "powered by" DEFAULT. The reference defaults the caption to its own brand.
 *    Here the caption is whatever `caption` says and nothing at all when it is
 *    unset, because defaulting somebody else's advertising into a customer's page
 *    is a decision for the person deploying it, not for this file.
 *
 * 7. A MUTE CONTROL, AND A PILL 24 PX WIDER FOR IT (2026-09-01). The reference
 *    pill had no way to mute the microphone until 2026-09-04, when it gained the same disc; the chat flag's header does, and the
 *    two flags are one product. The control is a sibling of the pill rather than
 *    a child — a button cannot contain a button — laid over a slot the live row
 *    leaves for it; see the geometry constants beside `PILL_PAD`. The same change
 *    made the connecting pill narrate the start sequence (stage-text.ts, the chat
 *    flag's four lines) instead of holding one word for four seconds.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY COLOUR AND DIMENSION HERE IS AN INLINE STYLE
 *
 * There is not one Tailwind class in this file, which looks wrong next to the rest
 * of `web/` and is not. This component is bundled into the embed and mounted inside
 * a shadow root, where `:root` tokens and compiled utility classes from the page's
 * own stylesheet do not reach. The embed DOES adopt `src/embed/embed.css` into that
 * root — but the flag is the one variant that is a brand object rather than a themed
 * one, and a customer's yellow tab must not turn into an unstyled grey rectangle
 * because a stylesheet failed to load, a CSP blocked it, or somebody mounted this
 * component somewhere new. So everything load-bearing is an inline style and this
 * file depends on no CSS whatsoever. That includes three things that are easy to
 * forget:
 *   - `fontFamily: FONT_STACK` — explicit, never 'inherit'. A UA-styled `<button>`
 *     does not inherit the page font, and inheriting was the wrong wish anyway:
 *     this widget renders on pages whose typography we do not control (ServiceNow's
 *     Service Portal made the label small and alien), so the stack is the system
 *     one, stated here so it holds even with no stylesheet at all. Font SIZES are
 *     px, never rem — rem resolves against the HOST page's `<html>` font-size even
 *     inside a shadow root, and Service Portal sets it to 10px;
 *   - `boxSizing: 'border-box'`, since there is no reset to rely on;
 *   - the focus ring, which cannot be a `:focus-visible` rule with no stylesheet
 *     and is instead a two-tone box-shadow toggled from a `matches(':focus-visible')`
 *     check in `onFocus`. Two tones because we do not know whether the host page
 *     behind the ring is white or black.
 *
 * The same reasoning is why the narrow-screen switch is a `matchMedia` listener and
 * not an `sm:` variant: a Tailwind breakpoint is a compiled media query in a
 * stylesheet, and this file depends on no stylesheet.
 */
import { useEffect, useState, type CSSProperties } from 'react'
import { FlagBars } from './flag-bars'
import { MicGlyph, MicOffGlyph } from './mic-glyphs'
import { connectingText, withTakeover } from './stage-text'
import { isLive, type VoiceControl, type VoicePhase, type VoiceWidgetCommon } from './types'

/**
 * The widget's own type, matching `.vai-root` in embed.css. Inline — like every
 * other load-bearing style in this file — so the flag is typographically ours even
 * when no stylesheet loaded at all.
 */
const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"

/** The reference's yellow and its red, kept to the hex rather than a token. */
const ACCENT = '#fac800'
const ON_ACCENT = '#000000'
const ERROR_BG = '#fb2c36'
const ERROR_FG = '#ffffff'

/** Collapsed tab, and the landscape pill it becomes. The tab is straight from
 *  the reference; the pill is the reference's 180 plus 24 px, added 2026-09-01
 *  for the mute control — see `MUTE_SIZE` — so the meters did not have to
 *  shrink to make room for it. */
const TAB_W = 40
const TAB_H = 160
const PILL_W = 204
const PILL_H = 68
/** The error pill is wider because a sentence has to fit in it. */
const ERROR_W = 220

/**
 * THE LIVE ROW'S GEOMETRY, stated as numbers because the mute control depends on
 * it. The row inside the pill is: stop glyph, gap, two meters, gap, and a slot
 * the mute button sits over. The mute button is NOT in the row — a `<button>`
 * cannot contain a `<button>`, and the pill is one — so it is a sibling of the
 * pill, absolutely positioned over the slot, and its position is computed from
 * these constants rather than measured. Everything here is px (never rem — see
 * the header), so the arithmetic holds on every host page.
 */
const PILL_PAD = 12
const ROW_GAP = 8
const STOP_SIZE = 40
/** Each meter. 44 rather than FlagBars' default 50, so the row fits the pill
 *  with 4 px to spare on each side. */
const METER_W = 44
/** The gap between the two meters — FlagBars' own default, restated so the
 *  arithmetic below does not depend on a default it cannot see. */
const METER_GAP = 4
const MUTE_SIZE = 24
const ROW_W = STOP_SIZE + ROW_GAP + (2 * METER_W + METER_GAP) + ROW_GAP + MUTE_SIZE
/** From the pill's page-facing edge to the mute slot: the row is centred, so
 *  half of what it does not fill. 16 px at the defaults. */
const MUTE_INSET = (PILL_W - ROW_W) / 2
/** The caption's line box (10.4 px at line-height 1.1) and the column gap above
 *  it; with a caption drawn, the row sits this much above the pill's centre. */
const CAPTION_LINE = 10.4 * 1.1
const COLUMN_GAP = 2

/** Corner radius: 14 px as a tab, 10 px as a pill. Only the two corners facing
 *  into the page get it; the edge-facing side stays square so it butts against the
 *  viewport with no sliver of page showing through. */
const R_TAB = 14
const R_PILL = 10

/**
 * The stand-in for `{ type: 'spring', stiffness: 300, damping: 30 }`.
 *
 * That spring is underdamped — damping ratio ≈ 0.87 — so it overshoots by a couple
 * of per cent and settles at about 400 ms. A `cubic-bezier` whose third control
 * point is above 1 overshoots the same way, and 400 ms lands the settle in the same
 * place. It is not the same curve; it is close enough that nobody has picked the
 * difference side by side, and it costs nothing.
 */
const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)'
const SPRING_MS = 400
/** The label's rotate/fade. The reference uses a plain 0.3 s tween; so does this. */
const FADE_MS = 300

/** Below this the pill becomes a bottom bar. 480 px is the widest phone in
 *  portrait; a small tablet keeps the side pill. */
const NARROW_MAX_PX = 480

/**
 * `theme` is omitted rather than accepted-and-ignored, the same refusal the
 * conversation bar makes: the light/dark switch paints by putting a class on a
 * wrapper for `embed.css`'s tokens to key off, and there is not one class name in
 * this file. The flag is a brand object — `accent` and `onAccent` ARE its livery,
 * and a yellow tab does not have a dark mode. The config editor stopped offering the
 * field on 2026-08-25; this is the same statement in the type, where a caller reads
 * it. `endLabel` STAYS: this widget paints it, in the expanded pill.
 */
export interface FlagButtonProps extends Omit<VoiceWidgetCommon, 'theme'> {
  control: VoiceControl
  side?: 'left' | 'right'
  offsetY?: string
  /** Collapsed tab size. Default 40 × 160. */
  width?: number
  height?: number
  /** z-index. Default 2147483000 — see the note below. */
  zIndex?: number
}

/**
 * Very nearly the top of the int32 stack, with ~600 left above it.
 *
 * This thing renders on other people's websites, over cookie banners, sticky
 * headers and chat widgets that all picked 9999 and thought they had won. So the
 * default has to be a number nobody reaches for by hand. The headroom is
 * deliberate: it leaves room for our own future overlay to sit ABOVE the flag
 * without another round of escalation, and a host who genuinely needs their modal
 * on top can pass a lower value rather than fighting us.
 */
const DEFAULT_Z = 2147483000

export function FlagButton({
  control,
  side = 'left',
  offsetY = '50%',
  width = TAB_W,
  height = TAB_H,
  zIndex = DEFAULT_Z,
  label = 'Click to Talk',
  endLabel = 'End call',
  accent = ACCENT,
  onAccent = ON_ACCENT,
  caption,
}: FlagButtonProps) {
  const phase = control.phase
  const narrow = useMediaQuery(`(max-width: ${NARROW_MAX_PX}px)`)
  const still = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [ring, setRing] = useState(false)
  const [hover, setHover] = useState(false)

  /**
   * Expanded for everything except the two resting states.
   *
   * The reference only expands AFTER `connect()` resolves, which means the two most
   * uncertain seconds of the whole interaction — the ones where a person is
   * wondering whether their click registered — look identical to not having
   * clicked. Expanding on `connecting` and showing the orb spinning is the whole
   * reason the phase exists.
   *
   * `ended` collapses, including when the server ended it rather than the user.
   * That is the reference's behaviour and it is right: the tab is the resting
   * state, and a spent pill sitting open is a call the user has to dismiss.
   */
  const expanded = phase !== 'idle' && phase !== 'ended'
  const failed = phase === 'error'
  /** Connecting is the only "wait" a visitor sees; the hang-up's own teardown is
   *  not shown at all (see `VoicePhase`). */
  const busy = phase === 'connecting'
  const live = isLive(phase)
  /** Expanded, on a phone: a bar across the bottom instead of a side pill. */
  const bar = narrow && expanded

  const bg = failed ? ERROR_BG : accent
  const fg = failed ? ERROR_FG : onAccent

  function onClick() {
    // Idempotent on the session's side, but a button that still looks pressable
    // mid-connect invites the double click in the first place. The cost of this,
    // stated plainly: a connect that hangs cannot be cancelled from here until it
    // fails on its own. The reference allowed the cancel and allowed the double
    // start; between the two faults, the un-cancellable one is recoverable.
    if (busy) return
    if (live) control.stop()
    else control.start()
  }

  // --- geometry -------------------------------------------------------------
  const r = expanded ? R_PILL : R_TAB
  const radius = bar
    ? `${r}px ${r}px 0 0`
    : side === 'left'
      ? `0 ${r}px ${r}px 0`
      : `${r}px 0 0 ${r}px`

  /** The horizontal offset points away from the edge the tab is stuck to, so it
   *  flips with the side. On the bottom bar it points up instead. */
  const sx = side === 'left' ? -1 : 1
  const shadow = bar
    ? '0 0 20px rgba(0,0,0,0.4), 0 -6px 15px rgba(0,0,0,0.2)'
    : expanded
      ? `0 0 20px rgba(0,0,0,0.4), ${6 * sx}px 0 15px rgba(0,0,0,0.2)`
      : `0 0 15px rgba(0,0,0,0.3), ${4 * sx}px 0 10px rgba(0,0,0,0.1)`

  /** Two tones, so the ring is visible whether the host page behind it is light or
   *  dark. Appended to the drop shadow because a box can only have one of these. */
  const ringShadow = ring ? `, 0 0 0 2px ${fg}, 0 0 0 5px ${bg}` : ''

  const container: CSSProperties = bar
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex, display: 'flex' }
    : {
        position: 'fixed',
        ...(side === 'left' ? { left: 0 } : { right: 0 }),
        // `offsetY` names the CENTRE of the tab, not its top — which is why the
        // default '50%' centres it. The reference's `top: 200px` with a -50%
        // translate had the same meaning, so a customer's existing offset carries
        // over unchanged.
        top: offsetY,
        transform: 'translateY(-50%)',
        zIndex,
        display: 'flex',
      }

  const button: CSSProperties = {
    // No reset exists inside the shadow root, so everything a UA stylesheet would
    // have opinions about is stated.
    boxSizing: 'border-box',
    appearance: 'none',
    border: 0,
    margin: 0,
    padding: 0,
    // Not the `font` shorthand: a shorthand followed by longhands depends on the
    // order React happens to assign them in. px, never rem — see the header.
    fontFamily: FONT_STACK,
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    width: bar ? '100%' : expanded ? (failed ? ERROR_W : PILL_W) : width,
    height: bar ? PILL_H : expanded ? PILL_H : height,
    borderRadius: radius,
    background: bg,
    color: fg,
    boxShadow: shadow + ringShadow,
    cursor: busy ? 'progress' : 'pointer',
    outline: 'none',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    // A cheap stand-in for the `:hover` rule we cannot write. Skipped while busy so
    // a non-clickable tab does not respond to the pointer.
    filter: hover && !busy ? 'brightness(0.94)' : 'none',
    transition: still
      ? 'none'
      : [
          `width ${SPRING_MS}ms ${SPRING}`,
          `height ${SPRING_MS}ms ${SPRING}`,
          `border-radius ${FADE_MS}ms ease`,
          'background 200ms ease',
          `box-shadow ${FADE_MS}ms ease`,
          `filter 150ms ease`,
        ].join(', '),
  }

  return (
    <div style={container}>
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel(phase, label, endLabel)}
        aria-disabled={busy || undefined}
        // `:focus-visible` needs a stylesheet; asking the element whether it
        // matches does not, and gives the same answer.
        onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
        onBlur={() => setRing(false)}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        style={button}
      >
        {/*
          Both layers stay mounted and cross-fade. Swapping them out, as the
          reference does, means the label vanishes on the same frame the box starts
          growing and the morph reads as two unrelated events. Both are
          `aria-hidden` because the button already has a name.

          `visibility` IS IN THE TRANSITION LIST AND THAT IS THE WHOLE TRICK: it is a
          discrete property, so it flips at the START of the transition when becoming
          visible and at the END when becoming hidden. The fade still plays, and once
          it has played the faded-out layer stops being painted at all — which is
          what keeps the orb inside it from compositing behind a collapsed tab on
          every page this widget is installed on.
        */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            opacity: expanded ? 0 : 1,
            visibility: expanded ? 'hidden' : 'visible',
            transform: `rotate(${expanded ? 0 : side === 'left' ? -90 : 90}deg)`,
            transition: still
              ? 'none'
              : `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease, visibility ${FADE_MS}ms ease`,
          }}
        >
          {label}
        </span>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: expanded ? 1 : 0,
            visibility: expanded ? 'visible' : 'hidden',
            transition: still ? 'none' : `opacity ${FADE_MS}ms ease, visibility ${FADE_MS}ms ease`,
          }}
        >
          {failed ? (
            <ErrorBody message={control.error} bar={bar} />
          ) : (
            <LiveBody
              control={control}
              phase={phase}
              bar={bar}
              side={side}
              caption={caption}
              endLabel={endLabel}
              showStop={live}
              onAccent={onAccent}
            />
          )}
        </span>
      </button>
      {/*
        THE MUTE CONTROL. A sibling of the pill, not a child — see the geometry
        note by `PILL_PAD` — laid over the slot the live row leaves for it. Only
        while the call is live: there is nothing to mute before, and a collapsed
        tab must not grow a second focus stop.
      */}
      {live && !failed ? (
        <MuteButton
          control={control}
          side={side}
          bar={bar}
          accent={accent}
          onAccent={onAccent}
          still={still}
          centerY={caption ? (PILL_H - (STOP_SIZE + COLUMN_GAP + CAPTION_LINE)) / 2 + STOP_SIZE / 2 : PILL_H / 2}
        />
      ) : null}
      {/* Outside the button, so the announcement is not entangled with the
          button's own name, and visually hidden without a stylesheet. */}
      <span aria-live="polite" style={SR_ONLY}>
        {withTakeover(control, statusSentence(phase, control.error))}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The expanded contents
// ---------------------------------------------------------------------------

function LiveBody({
  control,
  phase,
  bar,
  side,
  caption,
  endLabel,
  showStop,
  onAccent,
}: {
  control: VoiceControl
  phase: VoicePhase
  bar: boolean
  side: 'left' | 'right'
  caption: string | undefined
  endLabel: string
  showStop: boolean
  onAccent: string
}) {
  // On the right-hand edge the row reverses, so the stop glyph stays the thing
  // nearest the screen edge and the meters stay the thing nearest the page. On
  // the bottom bar there is no edge to be near, so it reads left-to-right.
  const dir: CSSProperties['flexDirection'] = !bar && side === 'right' ? 'row-reverse' : 'row'
  const connecting = phase === 'connecting'
  /**
   * While connecting, the pill narrates the start sequence — the same four lines
   * the chat flag's placeholder speaks (stage-text.ts) — instead of holding one
   * "Connecting…" that reads as stuck by the third second. A control that
   * reports no stage (a state preview, a test) gets the plain word.
   */
  const word = connecting ? connectingText(control.stage, 'Connecting…') : endLabel
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: COLUMN_GAP,
        width: '100%',
        height: '100%',
        padding: bar ? '0 16px' : `0 ${PILL_PAD}px`,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ display: 'flex', flexDirection: dir, alignItems: 'center', gap: bar ? 14 : ROW_GAP }}>
        {showStop ? <StopGlyph /> : null}
        {/*
          The meters are the tab's own ink (`onAccent`), because the reference drew
          BLACK bars on the yellow and the flag is a two-colour object — a third
          colour is one the design never had. They render only while the call is
          LIVE: during `connecting`/`ending` there is no audio to meter and ten
          floor-height bars read as "broken", so those phases show the word.
        */}
        {showStop ? (
          <FlagBars control={control} active={showStop} color={onAccent} meterWidth={METER_W} gap={METER_GAP} />
        ) : null}
        {/*
          The mute slot: empty, the mute button's size, so the row is centred WITH
          the button's footprint and the button — a sibling of the pill, see
          FlagButton — lands exactly over it. On the bottom bar the button sits at
          the bar's end instead and the row needs no slot.
        */}
        {showStop && !bar ? <span aria-hidden style={{ width: MUTE_SIZE, height: MUTE_SIZE, flexShrink: 0 }} /> : null}
        {/* The word fits on the bar, where there is a screen's width of room, and
            stands alone in the pill while connecting — the one stretch with neither
            glyph nor meters. The stage lines are longer than "End call", so in the
            pill they are set smaller and allowed a second line. */}
        {bar || !showStop ? (
          <span
            style={{
              fontSize: bar ? '16px' : connecting ? '12px' : '13px',
              fontWeight: !bar && connecting ? 600 : 700,
              lineHeight: 1.25,
              whiteSpace: !bar && connecting ? 'normal' : 'nowrap',
              textAlign: 'center',
              maxWidth: '100%',
            }}
          >
            {word}
          </span>
        ) : null}
      </span>
      {caption ? (
        <span
          style={{
            fontSize: '10.4px',
            fontWeight: 100,
            lineHeight: 1.1,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {caption}
        </span>
      ) : null}
    </span>
  )
}

/**
 * The failure state. White triangle, white 11 px text, on the reference's red.
 *
 * The reference cut the message at 26 characters with `substring`, which produces
 * things like "Microphone access is bloc". These messages are already written for
 * an end user and already short (see `friendlyError` in the adapter), so they are
 * allowed to wrap and are clamped by the box instead of by the string.
 */
function ErrorBody({ message, bar }: { message: string | null; bar: boolean }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: '100%',
        padding: '0 14px',
        boxSizing: 'border-box',
      }}
    >
      <WarningGlyph />
      <span
        style={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: 1.25,
          color: ERROR_FG,
          textAlign: bar ? 'center' : 'left',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 4,
          overflow: 'hidden',
        }}
      >
        {message ?? 'The call could not be connected. Please try again.'}
      </span>
    </span>
  )
}

/**
 * The mute toggle: a 24 px disc in the pill's ink, hollow while the microphone
 * is open and filled while it is muted — the same two-colour object as the rest
 * of the flag, so "is it on?" is answerable without a third colour. It is the
 * chat flag's mute control brought to the pill, with the chat flag's glyphs.
 *
 * Positioned from the constants beside `PILL_PAD`: over the slot the live row
 * leaves for it on the side pill (`MUTE_INSET` from the page-facing edge, which
 * flips with `side` because the row reverses), and at the right-hand end on the
 * bottom bar, whose row is centred in a screen's width and leaves no fixed slot.
 * `centerY` is the row's centre line, which sits above the pill's middle when a
 * caption is drawn beneath it.
 */
function MuteButton({
  control,
  side,
  bar,
  accent,
  onAccent,
  still,
  centerY,
}: {
  control: VoiceControl
  side: 'left' | 'right'
  bar: boolean
  accent: string
  onAccent: string
  still: boolean
  centerY: number
}) {
  const [ring, setRing] = useState(false)
  const [hover, setHover] = useState(false)
  const muted = control.muted
  const label = muted ? 'Unmute the microphone' : 'Mute the microphone'
  const at: CSSProperties = bar ? { right: 16 } : side === 'left' ? { right: MUTE_INSET } : { left: MUTE_INSET }
  return (
    <button
      type="button"
      onClick={control.toggleMute}
      title={label}
      aria-label={label}
      aria-pressed={muted}
      onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setRing(false)}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        position: 'absolute',
        top: centerY,
        transform: 'translateY(-50%)',
        ...at,
        boxSizing: 'border-box',
        appearance: 'none',
        margin: 0,
        padding: 0,
        width: MUTE_SIZE,
        height: MUTE_SIZE,
        borderRadius: '50%',
        border: `1.5px solid ${onAccent}`,
        background: muted ? onAccent : 'transparent',
        color: muted ? accent : onAccent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        outline: 'none',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        // Resting a little quieter than the stop glyph beside it; full strength
        // when hovered, and when it is the thing that is on.
        opacity: hover || muted ? 1 : 0.8,
        // Two tones, as on the pill, because the page behind could be either.
        boxShadow: ring ? `0 0 0 2px ${accent}, 0 0 0 4px ${onAccent}` : 'none',
        transition: still ? 'none' : 'opacity 150ms ease, background 150ms ease, color 150ms ease',
      }}
    >
      {muted ? <MicOffGlyph size={14} /> : <MicGlyph size={14} />}
    </button>
  )
}

/**
 * Stop: a filled disc with a rounded square knocked out of it, 40 px, in
 * `currentColor` so it is black on the yellow and white on the red without being
 * told. `fillRule="evenodd"` is what does the knocking out — the inner subpath is
 * a hole rather than a second filled shape, so the accent shows through it.
 */
function StopGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={40} height={40} fill="currentColor" aria-hidden focusable="false">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1.5a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21ZM9.75 8.25a1.5 1.5 0 0 0-1.5 1.5v4.5a1.5 1.5 0 0 0 1.5 1.5h4.5a1.5 1.5 0 0 0 1.5-1.5v-4.5a1.5 1.5 0 0 0-1.5-1.5h-4.5Z"
      />
    </svg>
  )
}

function WarningGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={24}
      height={24}
      fill={ERROR_FG}
      aria-hidden
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.6 3.1a1.62 1.62 0 0 1 2.8 0l8.1 14.05a1.62 1.62 0 0 1-1.4 2.43H3.9a1.62 1.62 0 0 1-1.4-2.43L10.6 3.1ZM12 8.1a.85.85 0 0 0-.85.85v4a.85.85 0 0 0 1.7 0v-4A.85.85 0 0 0 12 8.1Zm0 6.8a.95.95 0 1 0 0 1.9.95.95 0 0 0 0-1.9Z"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Wording, phases and the two media queries
// ---------------------------------------------------------------------------

/** The button's name says what a click will DO, which is not the same as what the
 *  widget is currently doing — that is the live region's job. */
function ariaLabel(phase: VoicePhase, label: string, endLabel: string): string {
  if (isLive(phase)) return endLabel
  if (phase === 'connecting') return 'Connecting, please wait'
  if (phase === 'error') return `${label} — retry`
  return label
}

/**
 * What the live region says, and — more importantly — what it refuses to say.
 *
 * `listening`, `thinking` and `speaking` alternate several times a minute. Piping
 * them into a polite live region would interrupt a screen reader user mid-turn with
 * "thinking… listening… speaking…" over and over, which is worse than silence: they
 * are trying to listen to the agent. So all three collapse to one sentence that
 * does not change, and only the coarse transitions — connecting, connected, ended,
 * failed — are announced.
 */
function statusSentence(phase: VoicePhase, error: string | null): string {
  switch (phase) {
    case 'idle':
      return ''
    case 'connecting':
      return 'Connecting.'
    case 'listening':
    case 'thinking':
    case 'speaking':
      return 'Connected. The call is running.'
    case 'ended':
      return 'Call ended.'
    case 'error':
      return error ?? 'The call could not be connected.'
  }
}

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  clipPath: 'inset(50%)',
}

/**
 * `matchMedia`, not a Tailwind variant, for the reason in the file header: inside
 * the embed's shadow root there is no compiled stylesheet for `sm:` to live in.
 *
 * The listener is also the honest way to do it even in the panel — the query is
 * about the VIEWPORT, and this component has no idea how wide its own container is.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    setMatches(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}
